import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { toast } from 'react-toastify';
import SmallModal from '../global/SmallModal';
import useGetGiftCard from '../../hooks/giftCards/useGetGiftCard';
import { formatDollarAmount } from '../../utils/global/helper';
import { formatGiftCardCode } from '../../helpers/gift-cards';
import { GiftCardActionOption } from '../../hooks/giftCards/useGiftCardActions';

interface Props {
  open: boolean;
  onClose: () => void;
  giftCardId: string | null | undefined;
  options?: GiftCardActionOption[];
}

function GiftCardDetailsModal({ open, onClose, giftCardId, options }: Props) {
  const theme = useTheme();
  const {
    data: giftCard,
    isFetching,
    isError,
  } = useGetGiftCard({
    giftCardId,
    enabled: open && !!giftCardId,
  });

  const handleCopyCode = async () => {
    if (!giftCard?.code) return;

    try {
      await navigator.clipboard.writeText(giftCard.code);
      toast.success('Gift card code copied to clipboard');
    } catch {
      toast.error('Failed to copy code');
    }
  };

  return (
    <SmallModal
      open={open}
      onClose={onClose}
      title="Gift Card Details"
      options={options}>
      <Box p={2}>
        {isFetching && (
          <Box
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight={150}>
            <CircularProgress color="primary" />
          </Box>
        )}

        {isError && (
          <Box
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight={100}>
            <Typography
              variant="body2"
              textAlign="center"
              sx={{ color: theme.palette.error.dark }}>
              There was an error loading the gift card. If the problem persists,
              please contact support.
            </Typography>
          </Box>
        )}

        {giftCard && !isFetching && (
          <Stack gap={3}>
            {/* Balance */}
            <Stack alignItems="center" gap={0.5}>
              <Typography variant="h1" component="p">
                {formatDollarAmount(giftCard.balance / 100, true)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Current balance
              </Typography>
            </Stack>

            {/* Gift Card Code */}
            <Box>
              <OutlinedInput
                fullWidth
                value={formatGiftCardCode(giftCard.code)}
                readOnly
                endAdornment={
                  <InputAdornment position="end" sx={{ pr: 1 }}>
                    <IconButton
                      onClick={handleCopyCode}
                      edge="end"
                      aria-label="copy gift card code">
                      <ContentCopy />
                    </IconButton>
                  </InputAdornment>
                }
                sx={{
                  bgcolor: theme.palette.lightGrey.main,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.lightOutlineGrey.main,
                  },
                  '& .MuiOutlinedInput-input': {
                    fontFamily: 'monospace',
                    fontSize: theme.typography.body1.fontSize,
                    letterSpacing: '0.1em',
                    overflowX: 'auto',
                  },
                }}
              />
            </Box>
          </Stack>
        )}
      </Box>
    </SmallModal>
  );
}

export default GiftCardDetailsModal;
